<template>
<input v-if="isUpdate" type="hidden" name="version_id" :value="ruleVersion.id">
<div v-if="rule && rule.created_at" class="column q-mb-lg">
  <div class="text-small">
    <q-icon name="schedule" size="xs" color="neutral-40" class="q-mr-lg" aria-hidden="true" />
    <span class="text-neutral-80">Aangemaakt: {{ $formatDateStandard(rule.created_at) }}</span>
  </div>
</div>

<div class="row q-col-gutter-y-md">
  <common-select
      v-if="!isTypeless"
      v-model="form.type"
      :options="ruleTypeOptions"
      emit-value
      class="col-12 col-md-4"
      label="Type"
      name="type"
      :error-message="$errorMessage('type')"
      :error="$hasError('type')"
      required
      map-options
      autofocus
  />
</div>
<rule-code-generator v-model:code="form.code"
                     v-model:categories="form.categories"
                     v-model:phases="form.phases"
                     v-model:leadingCategoryId="form.leading_category_id"
                     :editableCategories="editableCategories"
                     :allCategories="categories"
                     :allPhases="phases"
                     :generateCode="!rule.id && !dontGenerateCode" />

<div class="row q-col-gutter-y-md">
  <common-input
      v-model="form.summary"
      :label="fieldLabels.summary"
      name="summary"
      :error-message="$errorMessage('summary')"
      :error="$hasError('summary')"
      maxlength="140"
      hint="Max 140 karakters"
      class="col-12"
      required
  />
  <div class="col-12">
    <common-editor
        v-model="form.motivation"
        :label="fieldLabels.motivation"
        name="motivation"
        :error-message="$errorMessage('motivation')"
        :error="$hasError('motivation')"
        required
    />
  </div>
  <div class="col-12">
    <common-editor
        v-model="form.description"
        :label="fieldLabels.possible_solution"
        name="description"
        :error-message="$errorMessage('description')"
        :error="$hasError('description')"
    />
  </div>
  <div class="col-12" v-if="subcategoryOptions.length">
    <q-select v-model="form.subcategories"
              name="subcategories[]"
              :options="subcategoryOptions"
              label="Subcategorieën"
              class="text-neutral-90"
              multiple
              use-chips
              outlined
              map-options
              emit-value
              :disable="!form.leading_category_id"
              :error-message="$errorMessage('subcategories')"
              :error="$hasError('subcategories')">
      <template v-slot:selected-item="scope">
        <q-chip
            dense
            removable
            @remove="scope.removeAtIndex(scope.index)"
            :tabindex="scope.tabindex"
            class="common-chip"
        >
          {{ scope.opt.label }}
        </q-chip>
      </template>
    </q-select>
  </div>
  <div class="col-12" v-if="themes.length">
    <q-select v-model="form.themes"
              name="themes[]"
              :options="themes"
              label="Thema's"
              option-value="id"
              outlined
              multiple
              use-chips
              map-options
              emit-value
              :error-message="$errorMessage('themes')"
              :error="$hasError('themes')">
      <template v-slot:selected-item="scope">
        <q-chip
            dense
            removable
            @remove="scope.removeAtIndex(scope.index)"
            :tabindex="scope.tabindex"
            class="common-chip"
        >
          {{ scope.opt.title }}
        </q-chip>
      </template>
      <template v-slot:option="scope">
        <q-item v-bind="scope.itemProps">
          <q-item-section>
            <q-item-label>{{ scope.opt.title }}</q-item-label>
            <q-item-label caption>{{ scope.opt.description }}</q-item-label>
          </q-item-section>
        </q-item>
      </template>
    </q-select>
  </div>
  <div class="col-12">
    <div class="row q-mb-md q-col-gutter-x-md justify-items-center items-center"
         v-for="(link, index) in form.rule_links"
         :key="index">
      <common-input
          v-model="form.rule_links[index].name"
          :ref="`link-input-${index}`"
          :label="`Naam van externe link ${index + 1}`"
          :name="`rule_links[${index}][name]`"
          :error-message="$errorMessage(`rule_links.${index}.name`)"
          :error="$hasError(`rule_links.${index}.name`)"
          class="col-5"
      />
      <common-input
          v-model="form.rule_links[index].href"
          :label="`Externe link ${index + 1}`"
          :name="`rule_links[${index}][href]`"
          type="url"

          :error-message="$errorMessage(`rule_links.${index}.href`)"
          :error="$hasError(`rule_links.${index}.href`)"
          class="col-6"
          required
      />
      <input type="hidden" :value="form.rule_links[index].id"
             :name="`rule_links[${index}][id]`" />
      <div class="col-1">
        <icon-button
            icon="delete"
            color="negative"
            :aria-label="`Verwijder link ${index + 1}`"
            size="sm"
            round
            @click="deleteLink(index)"
        />
      </div>
    </div>
    <flat-button icon="add" @click="addLink" label="Externe link toevoegen" />
  </div>
</div>
<map-selection :mapIds="form.mapIds"
               v-if="!isMapless" />
<input type="hidden" name="version_id" :value="ruleVersion?.id" />
</template>

<script>
import { ruleType } from '~HIOR/constants'
import { config } from '~INSTANCE'

export default {
  props: {
    rule: {
      type: Object,
      required: false,
    },
    ruleVersion: {
      type: Object,
      required: false,
    },
    phases: {
      type: Array,
      default: null,
    },
    categories: {
      type: Array,
      default: null,
    },
    subcategories: {
      type: Object,
      default: null,
    },
    themes: {
      type: Array,
      default: null,
    },
    editableCategories: {
      type: Array,
      default: null,
    },
    isUpdate: {
      type: Boolean,
      default: false,
    },
    isTypeless: {
      type: Boolean,
      default: false,
    },
    isMapless: {
      type: Boolean,
      default: false,
    },
    dontGenerateCode: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      form: {
        categories: [],
        phases: [],
        links: [],
        leading_category_id: this.$old('leading_category_id', this.rule.leading_category_id),
        code: this.$old('code', this.rule.code),
        summary: this.$old('summary', this.ruleVersion.summary),
        motivation: this.$old('motivation', this.ruleVersion.motivation),
        description: this.$old('description', this.ruleVersion.description),
        rule_links: this.$old('rule_links', this.ruleVersion.links),
        mapIds: this.$old('maps', this.ruleVersion.maps || [])
            .map(map => parseInt(map?.id || map)),
        themes: this.$old('themes', this.ruleVersion.themes || [])
            .map(theme => parseInt(theme?.id || theme)),
        subcategories: this.$old('subcategories', this.ruleVersion.subcategories || [])
            .map(subcategory => parseInt(subcategory?.id || subcategory)),
      },
      toolbar: [
        ['bold', 'italic', 'unordered', 'ordered'],
      ],
      definitions: {
        bold: {
          tip: 'Dikgedrukt',
        },
        italic: {
          tip: 'Cursief',
        },
        unordered: {
          tip: 'Ongenummerde lijst',
        },
        ordered: {
          tip: 'Genummerde lijst',
        },
      },
    }
  },
  computed: {
    ruleTypeOptions () {
      return [
        {
          value: ruleType.TYPE_NORMAL,
          label: 'Standaard',
        },
        {
          value: ruleType.TYPE_FLEXIBLE,
          label: 'Flexibel',
        },
      ]
    },
    subcategoryOptions () {
      if (!this.form.leading_category_id || !this.subcategories) {
        return []
      }
      return this.form.categories.reduce((subcategories, category) => {
        return [
          ...subcategories,
          ...this.subcategories.filter(subcategory => subcategory.category_id === category.value),
        ]
      }, []).map(subcategory => ({
        value: subcategory.id,
        label: subcategory.title,
      }))
    },
    themeOptions () {
      return this.themes.map(theme => ({
        value: theme.id,
        label: theme.title,
      }))
    },
    fieldLabels () {
      return {
        summary: config.translations?.rule?.summary ?? 'Twitter tekst',
        motivation: config.translations?.rule?.motivation ?? 'Motivatie',
        possible_solution: config.translations?.rule?.possible_solution ?? 'Mogelijke oplossing',
      }
    },
  },
  methods: {
    async addLink () {
      const index = this.form.rule_links.length
      this.form.rule_links.push({ name: '', href: '' })
      await this.$nextTick()
      this.$refs[`link-input-${index}`][0].$el.focus()
    },
    deleteLink (index) {
      this.form.rule_links.splice(index, 1)
    },
  },
  mounted () {
    if (!this.$old('type') && !this.rule.type) {
      this.form.type = ruleType.TYPE_NORMAL
    } else {
      this.form.type = this.$old('type', this.ruleTypeOptions.find(option => option.value === this.rule.type))
    }

    if (this.$old('categories')) {
      this.form.categories = this.$old('categories').map(id => {
        const category = this.categories.find(category => category.id === parseInt(id))
        return {
          value: category.id,
          rule_code_letters: category.rule_code_letters,
          label: category.title,
        }
      })
    } else {
      this.form.categories = this.ruleVersion.categories.map(category => ({
        value: category.id,
        rule_code_letters: category.rule_code_letters,
        label: category.title,
      }))
    }
    if (this.$old('phases')) {
      this.form.phases = this.$old('phases').map(id => {
        const phase = this.phases.find(phase => phase.id === parseInt(id))
        return {
          value: phase.id,
          rule_code_letters: phase.rule_code_letters,
          label: phase.title,
        }
      })
    } else {
      this.form.phases = this.ruleVersion.phases.map(phase => ({
        value: phase.id,
        rule_code_letters: phase.rule_code_letters,
        label: phase.title,
      }))
    }
  },
}
</script>
